import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Paper, Backdrop, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  useTheme, useMediaQuery,
} from '@mui/material';
import { Cancel } from '@mui/icons-material';

import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import apiClient from 'utils/ApiClient';
import { useModulePermissions } from 'hooks/useModulePermissions';

import { LokiServer, LineFilter, QUICK_RANGES, LINE_FILTER_ENABLED } from './types';
import { LogToolbar } from './components/LogToolbar';
import { LogSearchBar, SearchMode } from './components/LogSearchBar';

const LS_KEY_SERVER = 'infralytiqs.logs.selectedServer';
const LS_KEY_LABELS = 'infralytiqs.logs.selectedLabels';
const LS_KEY_LINE_FILTER = 'infralytiqs.logs.lineFilter';
import { LabelBrowserDialog } from './components/LabelBrowserDialog';
import { TimePickerPopover } from './components/TimePickerPopover';
import { LogVolumeChart } from './components/LogVolumeChart';
import { LogStatsBar } from './components/LogStatsBar';
import { VirtualizedLogList } from './components/VirtualizedLogList';
import { useTimeRange } from './hooks/useTimeRange';
import { useLogQuery } from './hooks/useLogQuery';
import { useLiveReload } from './hooks/useLiveReload';
import { useChartData } from './hooks/useChartData';

export const Logs: React.FC = () => {
  const user = useSelector((state: RootState) => state.authentication.user);
  const currentUserId = user?._id;
  const { hasInfralytiqsAccess, isUserSuperAdmin } = useModulePermissions();
  const isAdmin = hasInfralytiqsAccess('admin') || isUserSuperAdmin;

  const [lokiServers, setLokiServers] = useState<LokiServer[]>([]);
  const [selectedServer, setSelectedServer] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState<Record<string, string[]>>({});
  const [isLive, setIsLive] = useState(false);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [expandedLogRows, setExpandedLogRows] = useState<Set<number>>(new Set());
  const [allRowsExpanded, setAllRowsExpanded] = useState(false);
  const [chartZoomRange, setChartZoomRange] = useState<{ from: number; to: number } | null>(null);
  const preZoomLiveRef = useRef(false);
  const restoredRef = useRef(false);
  const [searchText, setSearchText] = useState('');
  const [searchMode, setSearchMode] = useState<SearchMode>('filter');
  const [labelsRequiredDialogOpen, setLabelsRequiredDialogOpen] = useState(false);
  const [lineFilter, setLineFilter] = useState<LineFilter | null>(null);

  const theme = useTheme();
  const isMobilePortrait = useMediaQuery(theme.breakpoints.down('sm'));
  const isLandscapeMobile = useMediaQuery('(max-height: 500px) and (orientation: landscape)');
  const isCompact = isMobilePortrait || isLandscapeMobile;

  const selectedServerConfig = useMemo(
    () => lokiServers.find((s) => s._id === selectedServer),
    [lokiServers, selectedServer],
  );

  const timeRange = useTimeRange();

  const logQuery = useLogQuery({
    selectedServer,
    selectedLabels,
    // Line filtering is disabled for now (Loki isn't optimized for it), so never
    // apply a line filter to queries — even one restored from localStorage.
    lineFilter: LINE_FILTER_ENABLED ? lineFilter : null,
    getTimeRangeMs: timeRange.getTimeRangeMs,
  });

  const searchFilteredEntries = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term || searchMode === 'highlight') return logQuery.logEntries;
    return logQuery.logEntries.filter(e => e.line.toLowerCase().includes(term));
  }, [logQuery.logEntries, searchText, searchMode]);

  const { filteredLogEntries, chartTimeRange, volumeData, hasNonZeroVolume } = useChartData(
    searchFilteredEntries,
    chartZoomRange,
  );

  const activeSearchTerm = searchText.trim().toLowerCase();

  const { nextFetchAt } = useLiveReload({
    isLive,
    logQueryDone: logQuery.logQueryDone,
    hasEntries: logQuery.logEntries.length > 0,
    chartZoomRange,
    fetchNewerLogs: logQuery.fetchNewerLogs,
  });

  // Fetch Loki servers on mount and restore persisted selections
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/il/settings/module-configs');
        const configs: any[] = response.data || [];
        const lokiConfigs = configs.filter((c: any) => c.moduleType === 'Loki');
        const assignedConfigs = isAdmin
          ? lokiConfigs
          : lokiConfigs.filter((c: any) => {
              const ids: string[] = Array.isArray(c.assignedUserIds) ? c.assignedUserIds : [];
              return ids.includes(currentUserId);
            });
        const servers = assignedConfigs.map((c: any) => ({
          _id: c._id,
          name: c.name,
          moduleType: c.moduleType,
          labelValueSelections: c.labelValueSelections || undefined,
        }));
        setLokiServers(servers);

        const savedServer = localStorage.getItem(LS_KEY_SERVER);
        const serverExists = savedServer && servers.some((s) => s._id === savedServer);

        if (serverExists) {
          setSelectedServer(savedServer!);
          try {
            const raw = localStorage.getItem(LS_KEY_LABELS);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                setSelectedLabels(parsed);
              }
            }
          } catch { /* ignore corrupt data */ }
          try {
            const raw = localStorage.getItem(LS_KEY_LINE_FILTER);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && parsed.operator && parsed.value) {
                setLineFilter(parsed);
              }
            }
          } catch { /* ignore corrupt data */ }
        } else if (servers.length > 0) {
          setSelectedServer(servers[0]._id);
        } else {
          setSelectedServer('');
        }
      } catch {
        setLokiServers([]);
      } finally {
        restoredRef.current = true;
        setLoading(false);
      }
    })();
  }, [isAdmin, currentUserId]);

  // Persist selectedServer to localStorage (skip until initial restore completes)
  useEffect(() => {
    if (!restoredRef.current) return;
    if (selectedServer) {
      localStorage.setItem(LS_KEY_SERVER, selectedServer);
    }
  }, [selectedServer]);

  // Persist selectedLabels to localStorage (skip until initial restore completes)
  useEffect(() => {
    if (!restoredRef.current) return;
    localStorage.setItem(LS_KEY_LABELS, JSON.stringify(selectedLabels));
  }, [selectedLabels]);

  // Persist lineFilter to localStorage (skip until initial restore completes)
  useEffect(() => {
    if (!restoredRef.current) return;
    if (lineFilter) {
      localStorage.setItem(LS_KEY_LINE_FILTER, JSON.stringify(lineFilter));
    } else {
      localStorage.removeItem(LS_KEY_LINE_FILTER);
    }
  }, [lineFilter]);

  const handleServerChange = useCallback((newServer: string) => {
    setSelectedServer(newServer);
    setSelectedLabels({});
    setLineFilter(null);
    logQuery.reset();
    setIsLive(false);
    setExpandedLogRows(new Set());
    setAllRowsExpanded(false);
    setChartZoomRange(null);
    setSearchText('');
    setSearchMode('filter');
  }, [logQuery.reset]);

  const handleRunQuery = useCallback(async () => {
    if (Object.values(selectedLabels).flat().length === 0) {
      setLabelsRequiredDialogOpen(true);
      return;
    }
    await logQuery.handleRunQuery({
      setIsLive,
      setExpandedLogRows,
      setAllRowsExpanded,
      setChartZoomRange,
    });
  }, [logQuery.handleRunQuery, selectedLabels]);

  const handleToggleLive = useCallback(async () => {
    if (isLive) {
      setIsLive(false);
      return;
    }
    if (Object.values(selectedLabels).flat().length === 0) {
      setLabelsRequiredDialogOpen(true);
      return;
    }
    if (!logQuery.logQueryDone || logQuery.logEntries.length === 0) {
      await logQuery.handleRunQuery({
        setIsLive,
        setExpandedLogRows,
        setAllRowsExpanded,
        setChartZoomRange,
      });
    }
    setIsLive(true);
  }, [isLive, selectedLabels, logQuery.logQueryDone, logQuery.logEntries.length, logQuery.handleRunQuery]);

  const handleLabelDialogSubmit = useCallback((selections: Record<string, string[]>, newLineFilter: LineFilter | null) => {
    setSelectedLabels(selections);
    setLineFilter(newLineFilter);
    setLabelDialogOpen(false);
  }, []);

  const handleSearchReset = useCallback(() => {
    setSearchText('');
    setSearchMode('filter');
  }, []);

  const handleToggleRow = useCallback((id: number) => {
    setExpandedLogRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleToggleAllRows = useCallback(() => {
    if (allRowsExpanded) {
      setExpandedLogRows(new Set());
      setAllRowsExpanded(false);
    } else {
      const entries = chartZoomRange ? filteredLogEntries : logQuery.logEntries;
      setExpandedLogRows(new Set(entries.map((e) => e.id)));
      setAllRowsExpanded(true);
    }
  }, [allRowsExpanded, chartZoomRange, filteredLogEntries, logQuery.logEntries]);

  const handleChartZoom = useCallback((fromMs: number, toMs: number) => {
    preZoomLiveRef.current = isLive;
    if (isLive) setIsLive(false);
    setChartZoomRange({ from: fromMs, to: toMs });
  }, [isLive]);

  const handleClearChartZoom = useCallback(() => {
    setChartZoomRange(null);
    if (preZoomLiveRef.current) {
      setIsLive(true);
      preZoomLiveRef.current = false;
    }
  }, []);

  const pickerDates = timeRange.getPickerInitialDates();
  const selectedLabelCount = Object.values(selectedLabels).flat().length;
  const selectedServerObj = lokiServers.find(s => s._id === selectedServer);
  const isSelectedServerLoki = selectedServerObj?.moduleType === 'Loki';

  return (
    <Box sx={{ p: isCompact ? '10px' : '20px', display: 'flex', flexDirection: 'column', height: isCompact ? 'auto' : '100%', overflow: isCompact ? 'auto' : 'hidden', boxSizing: 'border-box' }}>
      <LogToolbar
        lokiServers={lokiServers}
        selectedServer={selectedServer}
        onServerChange={handleServerChange}
        serverListEmptyHint={
          isAdmin
            ? 'No Loki servers configured'
            : 'No servers assigned — contact your administrator'
        }
        selectedLabelCount={selectedLabelCount}
        onOpenLabelDialog={() => setLabelDialogOpen(true)}
        isSelectedServerLoki={!!isSelectedServerLoki}
        timeRangeLabel={timeRange.timeRangeLabel}
        onZoomIn={timeRange.handleZoomIn}
        onZoomOut={timeRange.handleZoomOut}
        onShiftBack={() => timeRange.shiftTime('back')}
        onShiftForward={() => timeRange.shiftTime('forward')}
        onTimePickerOpen={timeRange.handleTimePickerOpen}
        onRunQuery={handleRunQuery}
        logQueryLoading={logQuery.logQueryLoading}
        isLive={isLive}
        onToggleLive={handleToggleLive}
        nextFetchAt={nextFetchAt}
        chartZoomRange={chartZoomRange}
      />

      {logQuery.logQueryDone && logQuery.logEntries.length > 0 && (
        <LogSearchBar
          searchText={searchText}
          onSearchTextChange={setSearchText}
          searchMode={searchMode}
          onSearchModeChange={setSearchMode}
          onReset={handleSearchReset}
        />
      )}

      <LabelBrowserDialog
        open={labelDialogOpen}
        onClose={() => setLabelDialogOpen(false)}
        onSubmit={handleLabelDialogSubmit}
        selectedServer={selectedServer}
        getTimeRangeMs={timeRange.getTimeRangeMs}
        initialSelections={selectedLabels}
        initialLineFilter={lineFilter}
        allowedLabelValues={selectedServerConfig?.labelValueSelections}
        timeRangeLabel={timeRange.timeRangeLabel}
        timeRangeKey={timeRange.timeRangeKey}
        onQuickRangeSelect={(r) => {
          const full = QUICK_RANGES.find((q) => q.label === r.label);
          if (full) timeRange.handleQuickRangeSelect(full);
        }}
        onApplyCustomRange={timeRange.handleApplyCustomRange}
        getPickerInitialDates={timeRange.getPickerInitialDates}
        tzName={timeRange.tzName}
        tzString={timeRange.tzString}
      />

      <TimePickerPopover
        anchorEl={timeRange.anchorEl}
        onClose={() => timeRange.setAnchorEl(null)}
        timeRangeLabel={timeRange.timeRangeLabel}
        onQuickRangeSelect={(r) => {
          const full = QUICK_RANGES.find((q) => q.label === r.label);
          if (full) timeRange.handleQuickRangeSelect(full);
        }}
        onApplyCustomRange={timeRange.handleApplyCustomRange}
        initialFrom={pickerDates.from}
        initialTo={pickerDates.to}
        tzName={timeRange.tzName}
        tzString={timeRange.tzString}
      />

      <Dialog
        open={labelsRequiredDialogOpen}
        onClose={() => setLabelsRequiredDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Labels Required</DialogTitle>
        <DialogContent dividers sx={{ mt: 0, pt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            To query logs, please select at least one label filter. Click the <strong>Labels</strong> button in the toolbar to choose labels.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => {
              setLabelsRequiredDialogOpen(false);
              setLabelDialogOpen(true);
            }}
          >
            Select Labels
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setLabelsRequiredDialogOpen(false)}
            startIcon={<Cancel />}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Empty states */}
      {!selectedServer && lokiServers.length > 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Select a Loki server to start querying logs.
          </Typography>
        </Paper>
      )}

      {lokiServers.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {isAdmin
              ? 'No Loki servers configured. Go to Infralytiqs Settings to add a Loki server.'
              : 'No Loki servers are assigned to your account. Contact your company’s Infralytiqs administrator to request access.'}
          </Typography>
        </Paper>
      )}

      {selectedServer && !logQuery.logQueryDone && !logQuery.logQueryLoading && !logQuery.logQueryError && (
        <Paper sx={{ p: 4, textAlign: 'center', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {Object.keys(selectedLabels).length === 0
              ? 'Select labels and click "Run query" to fetch logs.'
              : 'Click "Run query" to fetch logs from the selected Loki server.'}
          </Typography>
        </Paper>
      )}

      {logQuery.logQueryLoading && (
        <Paper sx={{ p: 4, textAlign: 'center', minHeight: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <CircularProgress size={36} color="secondary" />
          <Typography variant="body1" color="text.secondary">
            Fetching logs from Loki server...
          </Typography>
        </Paper>
      )}

      {logQuery.logQueryError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => logQuery.setLogQueryError(null)}>
          {logQuery.logQueryError}
        </Alert>
      )}

      {/* Log Results */}
      {logQuery.logQueryDone && !logQuery.logQueryLoading && (
        <>
          {logQuery.logEntries.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No logs found in the selected time range with the current label filters.
              </Typography>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 2,
                overflow: 'hidden',
                flex: isCompact ? 'none' : 1,
                height: isCompact ? 500 : 'auto',
                minHeight: isCompact ? 500 : 0,
                display: 'flex', flexDirection: 'column',
              }}
            >
              {hasNonZeroVolume && !isCompact && (
                <LogVolumeChart
                  volumeData={volumeData}
                  chartTimeRange={chartTimeRange}
                  chartZoomRange={chartZoomRange}
                  onZoom={handleChartZoom}
                  onClearZoom={handleClearChartZoom}
                />
              )}

              <LogStatsBar
                totalLines={logQuery.logStats.totalLines}
                streams={logQuery.logStats.streams}
                filteredCount={filteredLogEntries.length}
                hasMoreLogs={logQuery.hasMoreLogs}
                isZoomed={!!chartZoomRange}
                commonLabels={logQuery.commonLabels}
                loadingMore={logQuery.loadingMoreLogs}
                loadingAllLogs={logQuery.loadingAllLogs}
                liveReloading={logQuery.liveReloading}
                allRowsExpanded={allRowsExpanded}
                onToggleAllRows={handleToggleAllRows}
                onLoadAllLogs={logQuery.loadAllLogs}
                onStopLoadAllLogs={logQuery.stopLoadAllLogs}
              />

              <VirtualizedLogList
                entries={filteredLogEntries}
                expandedRows={expandedLogRows}
                onToggleRow={handleToggleRow}
                hasMore={logQuery.hasMoreLogs}
                loadingMore={logQuery.loadingMoreLogs}
                onLoadMore={logQuery.loadMoreLogs}
                totalLines={logQuery.logStats.totalLines}
                infiniteScrollEnabled={!chartZoomRange}
                isCompact={isCompact}
                highlightTerm={searchMode === 'highlight' ? activeSearchTerm : ''}
              />
            </Paper>
          )}
        </>
      )}

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};
